import { useChannelMutate } from '@/app/components/collections/hooks'
import HoverableTooltip from '@/app/components/hoverable-tooltip'
import LockIcon from '@/app/icons/lock'
import StarIcon from '@/app/icons/star'
import TrashIcon from '@/app/icons/trash'
import { editChannelJson, removeGifsFromChannel } from '@/app/util/channel-api'
import { GridContext } from '@/components/grid/grid'
import GifOverlay from '@/components/grid/overlay/gif-overlay'
import { FlashMessageContext } from '@/context/flash-message'
import UserContext from '@/context/user-context'
import { cn } from '@/util/cn'
import { IChannel } from '@giphy/js-types'
import { GifOverlayProps } from '@giphy/react-components'
import { useContext, useState } from 'react'

export function generateId(prefix?: string): string {
    return `${prefix}${Math.random().toString(36).substring(2, 11)}`
}

type Props = { channel: IChannel } & GifOverlayProps
const ChannelOverlay = ({ gif, isHovered, width, channel }: Props) => {
    const { setGifs } = useContext(GridContext)
    const { showMessage } = useContext(FlashMessageContext)
    const [isRemoving, setIsRemoving] = useState(false)
    const mutate = useChannelMutate()
    const { user } = useContext(UserContext)
    const canEdit = !!(user && (user.is_staff || user.username === channel.user.username))
    if (!canEdit) {
        return <GifOverlay gif={gif} isHovered={isHovered} />
    }
    const isCurrentlyFeatured = channel.featured_gif?.id === gif.id
    return (
        <>
            {gif.is_hidden && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center select-none">
                    <LockIcon className="size-10 fill-giphyRed" />
                </div>
            )}
            {isHovered && (
                <div className="absolute top-2.5 left-2.5 flex items-center gap-3 rounded-md bg-giphyBlack/10 p-2">
                    <HoverableTooltip label={'Remove from Channel'}>
                        <TrashIcon
                            className="block size-5 cursor-pointer hover:fill-giphyRed"
                            onClick={async (e) => {
                                setIsRemoving(true)
                                e.preventDefault()
                                e.stopPropagation()
                                function onFailed() {
                                    showMessage({ message: 'There was an error removing the gif', type: 'error' })
                                    setIsRemoving(false)
                                }
                                try {
                                    const result = await removeGifsFromChannel([gif.id], channel.id)
                                    if (!result.ok) {
                                        onFailed()
                                    } else {
                                        setGifs((gifs) => gifs.filter((g) => g.id !== gif.id))
                                    }
                                } catch (e) {
                                    onFailed()
                                }
                            }}
                        />
                    </HoverableTooltip>
                    <HoverableTooltip
                        label={isCurrentlyFeatured ? 'Click another GIF to change the cover GIF' : 'Make Cover GIF'}
                    >
                        <StarIcon
                            onClick={async (e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                try {
                                    await editChannelJson(channel.id, {
                                        id: channel.id,
                                        parent: channel.parent,
                                        featured_gif: gif.id.toString(),
                                    })
                                    mutate({ ...channel, featured_gif: gif })
                                } catch (error) {
                                    showMessage({
                                        message: 'There was an error making the gif cover',
                                        type: 'error',
                                    })
                                }
                            }}
                            className={cn(
                                'block size-5 cursor-pointer hover:fill-giphyYellow',
                                isCurrentlyFeatured && 'cursor-default fill-giphyRed hover:fill-giphyRed'
                            )}
                        />
                    </HoverableTooltip>
                </div>
            )}
            <GifOverlay gif={gif} isHovered={isHovered} width={width}></GifOverlay>
            <div className={cn(isRemoving && 'pointer-events-none absolute inset-0 bg-giphyBlack/40')} />
        </>
    )
}

export default ChannelOverlay
